/**
 * DateRangePicker Component
 *
 * A date range picker with preset options (Today, Week, Month)
 * and custom date selection capability.
 */

import * as React from 'react';
import { Calendar, ChevronDown, X } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/base/Popover';
import { cn } from '@utils/cn';

export interface DateRange {
  from?: Date;
  to?: Date;
}

export type DateRangePreset = 'today' | 'week' | 'month' | 'custom' | 'all';

export interface DateRangePickerProps {
  /** Selected date range preset */
  preset?: DateRangePreset;
  /** Custom date range (when preset is 'custom') */
  customRange?: DateRange;
  /** Callback when selection changes */
  onChange: (preset: DateRangePreset, customRange?: DateRange) => void;
  /** Whether the picker is disabled */
  disabled?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Size variant */
  size?: 'sm' | 'md';
}

const presetLabels: Record<DateRangePreset, string> = {
  all: 'All Dates',
  today: 'Today',
  week: 'This Week',
  month: 'This Month',
  custom: 'Custom',
};

/**
 * Formats a date to a readable string
 */
function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Formats a date to YYYY-MM-DD for input fields
 */
function formatDateInput(date: Date): string {
  return date.toISOString().split('T')[0];
}

/**
 * Gets display text for current selection
 */
function getDisplayText(preset: DateRangePreset, customRange?: DateRange): string {
  if (preset === 'custom' && customRange?.from) {
    if (customRange.to) {
      return `${formatDate(customRange.from)} - ${formatDate(customRange.to)}`;
    }
    return formatDate(customRange.from);
  }
  return presetLabels[preset];
}

export function DateRangePicker({
  preset = 'all',
  customRange,
  onChange,
  disabled = false,
  placeholder = 'Select date',
  size = 'md',
}: DateRangePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [fromDate, setFromDate] = React.useState<string>(
    customRange?.from ? formatDateInput(customRange.from) : ''
  );
  const [toDate, setToDate] = React.useState<string>(
    customRange?.to ? formatDateInput(customRange.to) : ''
  );

  // Apply custom date range
  const handleApplyCustom = () => {
    if (fromDate) {
      const range: DateRange = {
        from: new Date(fromDate),
        to: toDate ? new Date(toDate) : undefined,
      };
      onChange('custom', range);
      setOpen(false);
    }
  };

  // Select a preset
  const handlePresetSelect = (selectedPreset: DateRangePreset) => {
    if (selectedPreset === 'custom') {
      // Don't close, let user set dates
      return;
    }
    setFromDate('');
    setToDate('');
    onChange(selectedPreset, undefined);
    setOpen(false);
  };

  // Clear selection
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFromDate('');
    setToDate('');
    onChange('all', undefined);
  };

  const hasSelection = preset !== 'all';
  const displayText = getDisplayText(preset, customRange);

  const sizeClasses = {
    sm: 'h-7 text-xs px-2',
    md: 'h-8 text-sm px-3',
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-lg border font-hanken transition-colors',
            sizeClasses[size],
            hasSelection
              ? 'border-[#061a61] bg-[#061a61]/5 text-[#061a61]'
              : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300',
            disabled && 'cursor-not-allowed opacity-50'
          )}
        >
          <Calendar className={cn('flex-shrink-0', size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5')} />
          <span className="truncate">{hasSelection ? displayText : placeholder}</span>
          {hasSelection ? (
            <X
              className={cn(
                'flex-shrink-0 hover:text-red-500',
                size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5'
              )}
              onClick={handleClear}
            />
          ) : (
            <ChevronDown
              className={cn(
                'flex-shrink-0 transition-transform',
                size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5',
                open && 'rotate-180'
              )}
            />
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent align="start" className="w-64 p-0">
        {/* Preset options */}
        <div className="border-b border-gray-100 p-2">
          <p className="mb-2 px-2 font-hanken text-xs font-medium text-gray-500">Quick Select</p>
          <div className="flex flex-wrap gap-1.5">
            {(['all', 'today', 'week', 'month'] as const).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => handlePresetSelect(p)}
                className={cn(
                  'rounded-md px-2.5 py-1 font-hanken text-xs transition-colors',
                  preset === p
                    ? 'bg-[#061a61] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                )}
              >
                {presetLabels[p]}
              </button>
            ))}
          </div>
        </div>

        {/* Custom date selection */}
        <div className="p-3">
          <p className="mb-2 font-hanken text-xs font-medium text-gray-500">Custom Range</p>
          <div className="space-y-2">
            <div>
              <label className="mb-1 block font-hanken text-xs text-gray-500">From</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full rounded-md border border-gray-200 px-2 py-1.5 font-hanken text-sm text-gray-800 focus:border-[#061a61] focus:outline-none focus:ring-1 focus:ring-[#061a61]"
              />
            </div>
            <div>
              <label className="mb-1 block font-hanken text-xs text-gray-500">To</label>
              <input
                type="date"
                value={toDate}
                min={fromDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full rounded-md border border-gray-200 px-2 py-1.5 font-hanken text-sm text-gray-800 focus:border-[#061a61] focus:outline-none focus:ring-1 focus:ring-[#061a61]"
              />
            </div>
            <button
              type="button"
              onClick={handleApplyCustom}
              disabled={!fromDate}
              className="w-full rounded-md bg-[#061a61] py-1.5 font-hanken text-sm font-medium text-white transition-colors hover:bg-[#0a2480] disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              Apply
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default DateRangePicker;
